import React, { useCallback, useMemo, useState } from "react";
import {
  Box,
  Button,
  FormField,
  Header,
  Select,
  SelectProps,
  SpaceBetween,
} from "@amzn/awsui-components-console";

import { CARGO_BUSINESS_UNITS } from "./cargoBusinessUnits";
import type { CargoPreScreenData } from "./types";

interface CargoPreScreenProps {
  /**
   * Invoked when the user submits the Cargo pre-screen with a business
   * unit selected. Receives the captured `{ businessUnit, schemaSegment }`
   * payload that the orchestrator persists to
   * `state.jsonForms.preScreen.cargo` and uses to build the Cargo schema
   * name (`Cargo-<schemaSegment>-v1_0_0`).
   */
  onPreScreenSubmit: (data: CargoPreScreenData) => void;
}

/**
 * Cargo pre-screen rendered by `JsonFormsClaimIntakeApp` for the `cargo` LOB.
 *
 * Renders a single Cloudscape `Select` populated from
 * `CARGO_BUSINESS_UNITS`. Submit is disabled until the user selects a
 * business unit; on submit, the component invokes `onPreScreenSubmit`
 * with the matching `{ businessUnit, schemaSegment }` payload.
 */
function CargoPreScreen({ onPreScreenSubmit }: CargoPreScreenProps): JSX.Element {
  const [selectedOption, setSelectedOption] = useState<SelectProps.Option | null>(null);

  const options = useMemo<SelectProps.Option[]>(
    () =>
      CARGO_BUSINESS_UNITS.map((bu) => ({
        label: bu.label,
        value: bu.value,
      })),
    []
  );

  const handleChange = useCallback(({ detail }: { detail: SelectProps.ChangeDetail }) => {
    setSelectedOption(detail.selectedOption);
  }, []);

  const handleSubmit = useCallback(() => {
    if (!selectedOption?.value) return;
    const businessUnit = CARGO_BUSINESS_UNITS.find((bu) => bu.value === selectedOption.value);
    if (!businessUnit) return;
    onPreScreenSubmit({
      businessUnit: businessUnit.value,
      schemaSegment: businessUnit.schemaSegment,
    });
  }, [onPreScreenSubmit, selectedOption]);

  const isSubmitDisabled = !selectedOption?.value;

  return (
    <Box padding="l" data-testid="cargo-pre-screen">
      <SpaceBetween size="m" direction="vertical">
        <Header variant="h2">Cargo claim intake</Header>
        <FormField
          label="Business Unit"
          description="Select the business unit responsible for the cargo claim."
          data-testid="cargo-pre-screen-business-unit-form-field"
        >
          <Select
            data-testid="cargo-pre-screen-business-unit-select"
            options={options}
            selectedOption={selectedOption}
            onChange={handleChange}
            placeholder="Select a business unit"
          />
        </FormField>
        <Button
          variant="primary"
          disabled={isSubmitDisabled}
          onClick={handleSubmit}
          data-testid="cargo-pre-screen-submit-button"
        >
          Continue
        </Button>
      </SpaceBetween>
    </Box>
  );
}

export default CargoPreScreen;
